import {
  Box,
  Button,
  Divider,
  Heading,
  HStack,
  IconButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  faChevronRight,
  faArrowLeft,
  faPlayCircle,
} from '@fortawesome/free-solid-svg-icons';
import React, {
  useState,
  Fragment,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import SparkelIcon from '../common/icon/SparkelIcon';
import {
  AlgorithmSettingsProps,
  algorithmSettingsRegistry,
  AutoShapeAlgorithmOptionsMap,
  getDefaultAlgorithmOptions,
} from '../auto-shapes/types';
import { ViewerMode } from '../common/viewer-mode';
import { AutoShapeAlgorithmType, Point3 } from 'src/gql/graphql';
import { getAlgorithmMetadata } from 'src/hooks/auto-shapes';

type AlgorithmState<T extends AutoShapeAlgorithmType> = {
  type: T;
  options: AutoShapeAlgorithmOptionsMap[T];
};

interface AutoshapePopoverProps {
  viewerMode: ViewerMode;
  createAutoShape: <T extends AutoShapeAlgorithmType>(
    name: string,
    algorithmType: T,
    algorithmInput: AutoShapeAlgorithmOptionsMap[T]
  ) => void;
  onClose: () => void;
  findNextAvailableAutoShapeName: (name: string) => string;
  modelUrn: string; // kept for API parity with caller, not directly used here
  modelId: string;
  globalOffset: Point3;
  sheetId: string;
  pageNumber: number;
  templateRect: { x: number; y: number; width: number; height: number };
  sensitivity: number;
}

export const AutoshapePopover = ({
  // modelUrn is intentionally not destructured to avoid unused variable linting
  viewerMode,
  createAutoShape,
  onClose,
  findNextAvailableAutoShapeName,
  modelId,
  globalOffset,
  sheetId,
  pageNumber,
  templateRect,
  sensitivity,
}: AutoshapePopoverProps) => {
  const [currentMenu, setCurrentMenu] = useState<'algorithms' | 'settings'>(
    'algorithms'
  );
  const [currentAlgorithmState, setCurrentAlgorithmState] =
    useState<AlgorithmState<AutoShapeAlgorithmType> | null>(null);

  const [isSettingsValid, setIsSettingsValid] = useState(false);

  const algorithms = useMemo(() => {
    return viewerMode === ViewerMode.Models
      ? Object.values(AutoShapeAlgorithmType).filter((algorithm) =>
          algorithm.includes('3D')
        )
      : Object.values(AutoShapeAlgorithmType).filter((algorithm) =>
          algorithm.includes('2D')
        );
  }, [viewerMode]);

  const renderSettings = useMemo(() => {
    if (!currentAlgorithmState?.type) return null;

    const type = currentAlgorithmState.type;
    const SettingsComponent = algorithmSettingsRegistry[
      type
    ] as React.ComponentType<AlgorithmSettingsProps<typeof type>>;

    return (
      <SettingsComponent
        value={currentAlgorithmState.options}
        onChange={(options) =>
          setCurrentAlgorithmState({
            type: currentAlgorithmState.type,
            options,
          })
        }
        onValidityChange={setIsSettingsValid}
      />
    );
  }, [currentAlgorithmState?.type, currentAlgorithmState?.options]);

  const handleClose = useCallback(() => {
    setCurrentMenu('algorithms');
    setCurrentAlgorithmState(null);
    onClose();
  }, [onClose]);

  // Allow external request to open Symbol Detection 2D settings directly
  useEffect(() => {
    const openSymbolDetection = () => {
      const algorithm = AutoShapeAlgorithmType.SymbolDetection_2D;
      setCurrentAlgorithmState({
        type: algorithm,
        options: getDefaultAlgorithmOptions(
          algorithm,
          modelId,
          globalOffset,
          [],
          sheetId,
          pageNumber,
          templateRect,
          sensitivity
        ),
      });
      setCurrentMenu('settings');
      // Once settings are visible, enter picking for the settings component
      window.setTimeout(() => {
        window.dispatchEvent(new Event('symbol-detection:start'));
      }, 0);
    };
    window.addEventListener(
      'autoshape:open:symbol-detection-2d',
      openSymbolDetection
    );
    return () =>
      window.removeEventListener(
        'autoshape:open:symbol-detection-2d',
        openSymbolDetection
      );
  }, [modelId, globalOffset, sheetId, pageNumber, templateRect, sensitivity]);

  // Hotkeys: Enter = Run (if settings open and valid), Space = Run and continue (re-enter picking)
  // We keep this local to the popover so it only runs when open
  // Note: we rely on the parent to open the popover via hotkeys in ShapesPanel
  // and on the Settings component to respond to picking events.
  // Minimal implementation: Enter triggers Run; Space triggers Run and emit start picking.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const maybeRun = useCallback(
    (continuePicking: boolean) => {
      if (
        !currentAlgorithmState ||
        currentMenu !== 'settings' ||
        !isSettingsValid
      )
        return;
      const name = findNextAvailableAutoShapeName(
        getAlgorithmMetadata(currentAlgorithmState.type).name
      );
      createAutoShape(
        name,
        currentAlgorithmState.type,
        currentAlgorithmState.options as AutoShapeAlgorithmOptionsMap[typeof currentAlgorithmState.type]
      );

      if (continuePicking) {
        // Close and immediately re-open Symbol Detection and start picking
        const reopen = () => {
          try {
            const addButton = document.querySelector(
              'button[aria-label="add-shape-options"]'
            ) as HTMLButtonElement | null;
            if (addButton) addButton.click();
          } catch {
            // noop
          }
          try {
            window.dispatchEvent(
              new Event('autoshape:open:symbol-detection-2d')
            );
          } catch {
            console.error(
              '[AutoShapes] autoshape:open:symbol-detection-2d not dispatched'
            );
          }
        };
        // Schedule reopen after closing
        window.setTimeout(reopen, 0);
      }

      handleClose();
    },
    [
      currentAlgorithmState,
      currentMenu,
      isSettingsValid,
      createAutoShape,
      findNextAvailableAutoShapeName,
      handleClose,
    ]
  );

  // Determine if a key event originated from an editable element (inputs, textareas, selects, or contentEditable)
  const isEventFromEditable = (event: KeyboardEvent): boolean => {
    const target = event.target as HTMLElement | null;
    if (!target) return false;
    const isEditableElement = (el: HTMLElement | null): boolean => {
      let node: HTMLElement | null = el;
      while (node) {
        if (node.isContentEditable) return true;
        const tag = node.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT')
          return true;
        const role = node.getAttribute('role');
        if (role === 'textbox' || role === 'combobox' || role === 'spinbutton')
          return true;
        node = node.parentElement;
      }
      return false;
    };
    return isEditableElement(target);
  };

  // Attach key handlers when the popover renders
  // We avoid useHotkeys here to keep the dependency minimal in this file
  // and to ensure the handler is active only while the popover is mounted
  // (MenuList is rendered)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isEventFromEditable(e)) return; // Don't steal keys from inputs/editables
      if (e.key === 'Enter') {
        e.preventDefault();
        maybeRun(false);
      } else if (e.key === ' ') {
        e.preventDefault();
        maybeRun(true);
      }
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () =>
      window.removeEventListener(
        'keydown',
        onKeyDown as EventListenerOrEventListenerObject,
        true
      );
  }, [maybeRun]);

  return (
    <MenuList
      zIndex={99}
      maxWidth={'300px'}
      maxHeight={'50vh'}
      overflow="hidden"
      paddingX={2}
    >
      <Box
        display="grid"
        gridTemplateColumns="200%" // Double width to accommodate two menus
        transition="transform 0.2s ease-in-out"
        transform={
          currentMenu === 'algorithms' ? 'translateX(0)' : 'translateX(-100%)'
        }
      >
        <Box display="grid" gridTemplateColumns="50% 50%">
          {/* Algorithms menu */}
          <Box
            height={currentMenu === 'algorithms' ? 'auto' : 0}
            overflow="hidden"
            background="inherit"
          >
            <HStack mb={1} padding={2} spacing={2}>
              <Heading size="xs">Auto Shapes</Heading>
            </HStack>

            {algorithms.map((algorithm, index) => {
              const algorithmMetadata = getAlgorithmMetadata(algorithm);
              return (
                <Fragment key={algorithm}>
                  <MenuItem
                    key={algorithm}
                    data-algo-key={String(algorithm)}
                    closeOnSelect={false}
                    onClick={() => {
                      setCurrentAlgorithmState({
                        type: algorithm,
                        options: getDefaultAlgorithmOptions(
                          algorithm,
                          modelId,
                          globalOffset,
                          [],
                          sheetId,
                          pageNumber,
                          templateRect,
                          sensitivity
                        ),
                      });
                      setCurrentMenu('settings');
                    }}
                  >
                    <HStack width="100%" justify="space-between">
                      <HStack>
                        <SparkelIcon
                          icon={algorithmMetadata.icon}
                          size="lg"
                          color={'gray.500'}
                          style={{
                            marginLeft: '12px',
                            marginRight: '12px',
                          }}
                        />
                        <VStack align="start" spacing={0}>
                          <Heading size="xs">{algorithmMetadata.name}</Heading>
                          <Text fontSize="xs" color="gray.500">
                            {algorithmMetadata.description}
                          </Text>
                        </VStack>
                      </HStack>
                      <SparkelIcon icon={faChevronRight} color={'gray.500'} />
                    </HStack>
                  </MenuItem>
                  {index !== algorithms.length - 1 && <Divider />}
                </Fragment>
              );
            })}
          </Box>

          {/* Settings menu */}
          {currentAlgorithmState?.type && (
            <Box
              height={currentMenu === 'settings' ? 'auto' : 0}
              overflow="hidden"
              background="inherit"
            >
              <HStack mb={1} px={2} spacing={2}>
                <IconButton
                  aria-label="Back"
                  colorScheme="gray"
                  icon={<SparkelIcon icon={faArrowLeft} color={'gray.500'} />}
                  variant="ghost"
                  size="xs"
                  onClick={() => setCurrentMenu('algorithms')}
                />
                <Heading size="xs">
                  {getAlgorithmMetadata(currentAlgorithmState.type).name}
                </Heading>
              </HStack>
              {
                <VStack paddingX={2}>
                  {renderSettings}
                  <Divider />
                  <Button
                    isDisabled={!isSettingsValid}
                    ml={'auto'}
                    size={'xs'}
                    borderRadius={'md'}
                    leftIcon={
                      <SparkelIcon icon={faPlayCircle} color={'inherit'} />
                    }
                    onClick={() => {
                      const name = findNextAvailableAutoShapeName(
                        getAlgorithmMetadata(currentAlgorithmState.type).name
                      );
                      createAutoShape(
                        name,
                        currentAlgorithmState.type,
                        currentAlgorithmState.options as AutoShapeAlgorithmOptionsMap[typeof currentAlgorithmState.type]
                      );
                      handleClose();
                    }}
                  >
                    Run
                  </Button>
                </VStack>
              }
            </Box>
          )}
        </Box>
      </Box>
    </MenuList>
  );
};
